import React, { useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, ReferenceArea, Cell,
  ComposedChart, Line,
} from 'recharts';

const formatGEX = (value) => {
  if (value === 0) return '0';
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (abs >= 1e9) return `${sign}${(abs / 1e9).toFixed(2)}B`;
  if (abs >= 100e6) return `${sign}${(abs / 1e6).toFixed(0)}M`;
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${sign}${(abs / 1e3).toFixed(0)}K`;
  return value.toFixed(0);
};

const VIEW_MODES = [
  { id: 'net', label: 'Net GEX' },
  { id: 'split', label: 'Call vs Put' },
];

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div style={{
      background: 'rgba(2,6,23,0.97)', border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: '14px', padding: '16px 18px', backdropFilter: 'blur(20px)',
      boxShadow: '0 20px 60px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04)',
      minWidth: '200px', fontFamily: "'Outfit', sans-serif",
    }}>
      <div style={{ fontSize: '13px', fontWeight: '800', color: '#f1f5f9', marginBottom: '12px', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '-0.5px' }}>
        Strike <span style={{ color: '#fbbf24' }}>${d.strike}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {[
          { label: 'Call GEX', val: d.call_gex, color: '#34d399' },
          { label: 'Put GEX', val: d.put_gex, color: '#f87171' },
        ].map(({ label, val, color }) => (
          <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '20px' }}>
            <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)', fontWeight: '600' }}>{label}</span>
            <span style={{ fontSize: '12px', color, fontWeight: '700', fontFamily: "'JetBrains Mono', monospace" }}>{formatGEX(val)}</span>
          </div>
        ))}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '8px', marginTop: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '11px', color: d.net_gex >= 0 ? '#34d399' : '#f87171', fontWeight: '800', letterSpacing: '0.5px' }}>NET GEX</span>
          <span style={{ fontSize: '16px', color: d.net_gex >= 0 ? '#34d399' : '#f87171', fontWeight: '900', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '-0.5px' }}>{formatGEX(d.net_gex)}</span>
        </div>
      </div>
    </div>
  );
};

const LvlLabel = (text, color, position = 'top') => ({
  value: text, fill: color, fontSize: 9, fontWeight: 800, position, offset: 5,
  fontFamily: "'JetBrains Mono', monospace",
});

function ViewToggle({ modes, active, onChange }) {
  return (
    <div style={{ display: 'inline-flex', background: 'rgba(255,255,255,0.04)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.07)', padding: '3px', gap: '2px' }}>
      {modes.map(m => {
        const on = active === m.id;
        return (
          <button key={m.id} onClick={() => onChange(m.id)} style={{
            background: on ? 'rgba(255,255,255,0.08)' : 'transparent',
            color: on ? '#f1f5f9' : '#475569',
            border: on ? '1px solid rgba(255,255,255,0.1)' : '1px solid transparent',
            borderRadius: '9px', padding: '7px 18px', fontSize: '12px',
            fontWeight: on ? '700' : '500', cursor: 'pointer',
            transition: 'all 0.2s ease', fontFamily: "'Outfit', sans-serif",
          }}>{m.label}</button>
        );
      })}
    </div>
  );
}

function LegendItem({ color, dashed, label, area }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: '#94a3b8', fontWeight: '600' }}>
      {area
        ? <span style={{ width: '14px', height: '10px', background: color, borderRadius: '2px', opacity: 0.5, display: 'inline-block' }} />
        : dashed
          ? <span style={{ width: '16px', height: '0', borderTop: '2px dashed ' + color, display: 'inline-block' }} />
          : <span style={{ width: '8px', height: '8px', borderRadius: '2px', background: color, display: 'inline-block' }} />
      }
      {label}
    </span>
  );
}

export default function GEXChart({ strikes, keyLevels }) {
  const [viewMode, setViewMode] = useState('net');

  if (!strikes?.length) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{ width: '48px', height: '48px', margin: '0 auto 14px', borderRadius: '14px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
        </div>
        <p style={{ color: '#334155', fontSize: '13px', fontFamily: "'Outfit', sans-serif" }}>No strike data available</p>
      </div>
    );
  }

  const { spot_price: spot, call_wall: cw, put_wall: pw, gamma_flip: gf } = keyLevels || {};

  const findClosest = (target) => {
    if (!target) return null;
    return strikes.reduce((c, s) => Math.abs(s.strike - target) < Math.abs(c.strike - target) ? s : c).strike;
  };

  const closestSpot = findClosest(spot);
  const closestCW = findClosest(cw);
  const closestPW = findClosest(pw);
  const closestGF = findClosest(gf);
  const chartH = strikes.length > 40 ? 470 : 420;

  const levelLines = () => (
    <>
      {closestPW && closestCW && <ReferenceArea x1={closestPW} x2={closestCW} fill="rgba(139,92,246,0.04)" stroke="none" />}
      {closestSpot && <ReferenceLine x={closestSpot} stroke="#fbbf24" strokeWidth={2} strokeDasharray="6 3" label={LvlLabel(`$${spot?.toFixed(0)}`, '#fbbf24', 'top')} />}
      {closestCW && closestCW !== closestSpot && <ReferenceLine x={closestCW} stroke="#34d399" strokeWidth={1.5} strokeDasharray="4 4" label={LvlLabel(`$${cw?.toFixed(0)}`, '#34d399', 'insideTopRight')} />}
      {closestPW && closestPW !== closestSpot && <ReferenceLine x={closestPW} stroke="#f87171" strokeWidth={1.5} strokeDasharray="4 4" label={LvlLabel(`$${pw?.toFixed(0)}`, '#f87171', 'insideTopLeft')} />}
      {closestGF && closestGF !== closestSpot && closestGF !== closestCW && closestGF !== closestPW && <ReferenceLine x={closestGF} stroke="#a78bfa" strokeWidth={1.5} strokeDasharray="3 5" label={LvlLabel(`$${gf?.toFixed(0)}`, '#a78bfa', 'insideBottomRight')} />}
      <ReferenceLine y={0} stroke="rgba(255,255,255,0.07)" strokeWidth={1} />
    </>
  );

  const axisProps = {
    xAxis: {
      dataKey: 'strike',
      tick: { fill: '#334155', fontSize: 10, fontFamily: "'JetBrains Mono', monospace" },
      tickFormatter: (v) => `$${v}`,
      interval: Math.max(0, Math.floor(strikes.length / 12)),
      axisLine: { stroke: 'rgba(255,255,255,0.05)' },
      tickLine: { stroke: 'rgba(255,255,255,0.04)' },
    },
    yAxis: {
      tick: { fill: '#334155', fontSize: 10, fontFamily: "'JetBrains Mono', monospace" },
      tickFormatter: formatGEX,
      axisLine: { stroke: 'rgba(255,255,255,0.05)' },
      tickLine: false,
      width: 58,
    },
  };

  return (
    <div>
      {/* Controls */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px', flexWrap: 'wrap', gap: '12px' }}>
        <ViewToggle modes={VIEW_MODES} active={viewMode} onChange={setViewMode} />
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          {viewMode === 'net' ? (
            <><LegendItem color="#34d399" label="Positive" /><LegendItem color="#f87171" label="Negative" /></>
          ) : (
            <><LegendItem color="#34d399" label="Call GEX" /><LegendItem color="#f87171" label="Put GEX" /></>
          )}
          <LegendItem color="#fbbf24" dashed label="Spot" />
          <LegendItem color="#34d399" dashed label="CW" />
          <LegendItem color="#f87171" dashed label="PW" />
          <LegendItem color="#a78bfa" dashed label="Flip" />
          <LegendItem color="rgba(139,92,246,0.25)" area label="Range" />
        </div>
      </div>

      {/* Chart */}
      <div style={{ width: '100%', height: chartH }}>
        <ResponsiveContainer>
          {viewMode === 'net' ? (
            <BarChart data={strikes} margin={{ top: 28, right: 28, left: 8, bottom: 16 }}>
              <defs>
                <linearGradient id="barPos" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#34d399" stopOpacity={0.95} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0.55} />
                </linearGradient>
                <linearGradient id="barNeg" x1="0" y1="1" x2="0" y2="0">
                  <stop offset="0%" stopColor="#ef4444" stopOpacity={0.95} />
                  <stop offset="100%" stopColor="#f87171" stopOpacity={0.55} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="2 6" stroke="rgba(255,255,255,0.03)" vertical={false} />
              <XAxis {...axisProps.xAxis} />
              <YAxis {...axisProps.yAxis} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.025)', radius: 4 }} />
              {levelLines()}
              <Bar dataKey="net_gex" radius={[4, 4, 0, 0]} maxBarSize={24}>
                {strikes.map((entry, i) => (
                  <Cell key={i} fill={entry.net_gex >= 0 ? 'url(#barPos)' : 'url(#barNeg)'} />
                ))}
              </Bar>
            </BarChart>
          ) : (
            <ComposedChart data={strikes} margin={{ top: 28, right: 28, left: 8, bottom: 16 }}>
              <defs>
                <linearGradient id="callBar" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#34d399" stopOpacity={0.8} />
                  <stop offset="100%" stopColor="#34d399" stopOpacity={0.3} />
                </linearGradient>
                <linearGradient id="putBar" x1="0" y1="1" x2="0" y2="0">
                  <stop offset="0%" stopColor="#f87171" stopOpacity={0.8} />
                  <stop offset="100%" stopColor="#f87171" stopOpacity={0.3} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="2 6" stroke="rgba(255,255,255,0.03)" vertical={false} />
              <XAxis {...axisProps.xAxis} />
              <YAxis {...axisProps.yAxis} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.025)' }} />
              {levelLines()}
              <Bar dataKey="call_gex" fill="url(#callBar)" radius={[3, 3, 0, 0]} maxBarSize={18} name="Call GEX" />
              <Bar dataKey="put_gex" fill="url(#putBar)" radius={[3, 3, 0, 0]} maxBarSize={18} name="Put GEX" />
              <Line dataKey="net_gex" stroke="#60a5fa" strokeWidth={2.5} dot={false} name="Net GEX" strokeDasharray="4 2" opacity={0.85} />
            </ComposedChart>
          )}
        </ResponsiveContainer>
      </div>

      {/* Level key pills */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '14px', paddingTop: '14px', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
        {[
          { label: 'Call Wall', color: '#34d399', desc: 'Resistance ceiling' },
          { label: 'Put Wall', color: '#f87171', desc: 'Support floor' },
          { label: 'Gamma Flip', color: '#a78bfa', desc: 'Regime pivot' },
          { label: 'Spot', color: '#fbbf24', desc: 'Current price' },
        ].map(({ label, color, desc }) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '5px 12px', background: color + '0a', border: '1px solid ' + color + '18', borderRadius: '8px' }}>
            <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: color, boxShadow: '0 0 5px ' + color + '70' }} />
            <span style={{ fontSize: '10px', fontWeight: '700', color, letterSpacing: '0.3px' }}>{label}</span>
            <span style={{ fontSize: '9px', color: '#475569' }}>{desc}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
